import { useState, useEffect, useMemo, useRef } from 'react';
import { shippingService } from '../services/shippingService';
import { SHIPMENT_STATUSES } from '../components/shipping/ShipmentStatusBadge';
import toast from 'react-hot-toast';
import api from '../services/api';

export function useShippingData() {
  const [shipments, setShipments] = useState([]);
  const [shipmentsLoading, setShipmentsLoading] = useState(false);
  const [stats, setStats] = useState({ total: 0, in_transit: 0, delivered: 0, cancelled: 0 });
  const [syncing, setSyncing] = useState(false);

  // Licznik sekwencji — ignoruje przeterminowane odpowiedzi loadShipments (L-race)
  const loadSeqRef = useRef(0);

  // Autorytatywna lista statusow "w drodze" — zsynchronizowana z backend
  // shipping_service.get_shipping_stats (in_transit). NIE zmieniac bez zmiany backendu.
  const IN_TRANSIT_STATUSES = [
    'confirmed', 'dispatched_by_sender', 'collected_from_sender', 'taken_by_courier',
    'adopted_at_source_branch', 'sent_from_source_branch', 'adopted_at_sorting_center',
    'sent_from_sorting_center', 'adopted_at_target_branch', 'out_for_delivery'
  ];

  const loadShipments = async () => {
    const seq = ++loadSeqRef.current;
    try {
      setShipmentsLoading(true);
      const response = await api.get('/shipping/shipments');
      const data = response.data;

      // Ignoruj odpowiedz, jesli wystartowal juz nowszy load (L-race)
      if (seq !== loadSeqRef.current) return;

      if (data.success) {
        const allShipments = data.data || [];
        setShipments(allShipments);

        const statsCalc = {
          total: allShipments.length,
          in_transit: allShipments.filter(s =>
            IN_TRANSIT_STATUSES.includes(s.status)
          ).length,
          delivered: allShipments.filter(s => s.status === 'delivered').length,
          cancelled: allShipments.filter(s => s.status === 'cancelled').length
        };
        setStats(statsCalc);
      }
    } catch (error) {
      if (seq !== loadSeqRef.current) return;
      console.error('Błąd pobierania przesyłek:', error);
    } finally {
      // Wygas spinner tylko dla najnowszego zadania
      if (seq === loadSeqRef.current) setShipmentsLoading(false);
    }
  };

  const handleSyncTracking = async () => {
    try {
      setSyncing(true);
      const response = await api.post('/shipping/shipments/sync-tracking');
      const data = response.data;
      if (data.success) {
        toast.success(`Zaktualizowano ${data.updated} przesyłek`);
        loadShipments();
      } else {
        toast.error('Błąd synchronizacji: ' + data.message);
      }
    } catch (error) {
      console.error('Błąd synchronizacji:', error);
      toast.error('Błąd synchronizacji numerów śledzenia');
    } finally {
      setSyncing(false);
    }
  };

  const handleRefreshStatus = async (shipment) => {
    try {
      await api.get(`/shipping/shipments/${shipment.inpost_id}/status`);
      loadShipments();
    } catch (error) {
      console.error('Błąd odświeżania:', error);
      toast.error(error.message || 'Błąd odświeżania statusu przesyłki');
    }
  };

  const handleDownloadLabel = async (shipment) => {
    try {
      const response = await api.get(`/shipping/shipments/${shipment.inpost_id}/label`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `etykieta_${shipment.tracking_number || shipment.inpost_id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.message || 'Błąd pobierania etykiety');
    }
  };

  const handleCancelShipment = async (shipment) => {
    const hasLabel = shipment.tracking_number && shipment.tracking_number !== shipment.inpost_id;
    let confirmMessage = 'Czy na pewno chcesz anulować tę przesyłkę?';
    if (hasLabel) {
      confirmMessage = `UWAGA: Dla tej przesyłki została już wygenerowana etykieta (${shipment.tracking_number})!\n\nCzy na pewno chcesz anulować? Etykieta stanie się nieważna.`;
    }

    if (!window.confirm(confirmMessage)) return;

    try {
      const response = await api.post(`/shipping/shipments/${shipment.inpost_id}/cancel`);
      const result = response.data;
      if (result.success) {
        toast.success('Przesyłka anulowana');
        loadShipments();
      } else {
        toast.error('Błąd: ' + (result.message || 'Nie udało się anulować przesyłki'));
      }
    } catch (error) {
      toast.error(error.message || 'Błąd połączenia z serwerem');
    }
  };

  return {
    shipments,
    shipmentsLoading,
    stats,
    syncing,
    loadShipments,
    handleSyncTracking,
    handleRefreshStatus,
    handleDownloadLabel,
    handleCancelShipment,
  };
}

export function useCreateShipment(onSuccess) {
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [serviceType, setServiceType] = useState('inpost_locker_standard');
  const [sourcePoint, setSourcePoint] = useState(null);
  const [targetPoint, setTargetPoint] = useState(null);
  const [dropoffPoint, setDropoffPoint] = useState(null);
  const [parcelSize, setParcelSize] = useState('small');
  const [comment, setComment] = useState('');
  const [codEnabled, setCodEnabled] = useState(false);
  const [codAmount, setCodAmount] = useState('');
  const [insuranceEnabled, setInsuranceEnabled] = useState(false);
  const [insuranceAmount, setInsuranceAmount] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createSuccess, setCreateSuccess] = useState(null);

  const resetForm = () => {
    setSelectedCustomer(null);
    setTargetPoint(null);
    setSourcePoint(null);
    setDropoffPoint(null);
    setComment('');
    setCodEnabled(false);
    setCodAmount('');
    setInsuranceEnabled(false);
    setInsuranceAmount('');
    setCreateError(null);
    setCreateSuccess(null);
  };

  const handleCreateShipment = async () => {
    if (!selectedCustomer) { setCreateError('Wybierz klienta'); return; }
    if ((serviceType === 'inpost_locker_standard' || serviceType === 'inpost_courier_locker') && !targetPoint) {
      setCreateError('Wybierz paczkomat docelowy'); return;
    }
    if (serviceType === 'inpost_locker_to_address' && !dropoffPoint) {
      setCreateError('Wybierz paczkomat nadawczy'); return;
    }
    // Wybor adresu dostawy — SPOJNIE wg flagi wysylka_inna (M-addr).
    // Gdy false -> adres glowny w calosci; gdy true -> komplet pol wysylka_*.
    // Bez mieszania per-pole (zaden fallback wysylka_x || adres_x).
    const useAltAddress = !!selectedCustomer.wysylka_inna;
    const deliveryAddress = useAltAddress
      ? {
          street: selectedCustomer.wysylka_ulica || '',
          city: selectedCustomer.wysylka_miasto || '',
          post_code: selectedCustomer.wysylka_kod_pocztowy || '',
        }
      : {
          street: selectedCustomer.adres || '',
          city: selectedCustomer.miasto || '',
          post_code: selectedCustomer.kod_pocztowy || '',
        };
    const needsAddress = serviceType === 'inpost_locker_to_address' ||
      serviceType === 'inpost_courier_standard' || serviceType === 'inpost_courier_express_1000';
    if (needsAddress && (!deliveryAddress.street || !deliveryAddress.city || !deliveryAddress.post_code)) {
      setCreateError(useAltAddress
        ? 'Alternatywny adres wysyłki jest niekompletny (ulica / kod / miasto)'
        : 'Klient nie ma uzupełnionego adresu dostawy');
      return;
    }

    // Walidacja COD (M-cod): zaznaczone -> kwota musi byc dodatnia; blokuj ujemne
    if (codEnabled) {
      const cod = parseFloat(codAmount);
      if (!Number.isFinite(cod) || cod <= 0) {
        setCreateError('Kwota pobrania (COD) musi być większa od 0'); return;
      }
    }
    if (insuranceEnabled) {
      const ins = parseFloat(insuranceAmount);
      if (!Number.isFinite(ins) || ins < 0) {
        setCreateError('Kwota ubezpieczenia nie może być ujemna'); return;
      }
    }

    try {
      setCreating(true);
      setCreateError(null);
      setCreateSuccess(null);

      const shipmentData = {
        receiver_first_name: selectedCustomer.imie || '',
        receiver_last_name: selectedCustomer.nazwisko || '',
        receiver_company: selectedCustomer.nazwa_firmy || '',
        receiver_phone: selectedCustomer.telefon,
        receiver_email: selectedCustomer.email,
        service: serviceType,
        parcel_template: parcelSize,
        comments: comment,
        auto_buy: true
      };

      if (serviceType === 'inpost_locker_standard' || serviceType === 'inpost_courier_locker') {
        shipmentData.target_point = targetPoint?.name;
      }
      if (serviceType === 'inpost_locker_to_address') {
        shipmentData.dropoff_point = dropoffPoint?.name;
        shipmentData.receiver_street = deliveryAddress.street;
        shipmentData.receiver_city = deliveryAddress.city;
        shipmentData.receiver_post_code = deliveryAddress.post_code;
      }
      if (serviceType === 'inpost_courier_standard' || serviceType === 'inpost_courier_express_1000') {
        shipmentData.receiver_street = deliveryAddress.street;
        shipmentData.receiver_city = deliveryAddress.city;
        shipmentData.receiver_post_code = deliveryAddress.post_code;
      }
      if (selectedCustomer.id) { shipmentData.customer_id = selectedCustomer.id; }
      if (codEnabled) { shipmentData.cod_amount = parseFloat(codAmount); }
      if (insuranceEnabled) { shipmentData.insurance_amount = parseFloat(insuranceAmount); }

      const response = await api.post('/shipping/shipments', shipmentData);
      const result = response.data;

      if (result.success) {
        setCreateSuccess(`Przesyłka utworzona! Numer: ${result.tracking_number || 'oczekuje na opłatę'}`);
        if (result.inpost_id) {
          try {
            const labelResponse = await api.get(`/shipping/shipments/${result.inpost_id}/label`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(labelResponse.data);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `etykieta_${result.tracking_number || result.inpost_id}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
          } catch (e) {
            // Label not yet available — ignore
          }
        }
        resetForm();
        if (onSuccess) onSuccess();
      } else {
        setCreateError(result.message || 'Błąd tworzenia przesyłki');
      }
    } catch (error) {
      setCreateError(error.message || 'Błąd połączenia z serwerem');
    } finally {
      setCreating(false);
    }
  };

  return {
    selectedCustomer, setSelectedCustomer,
    serviceType, setServiceType,
    sourcePoint, setSourcePoint,
    targetPoint, setTargetPoint,
    dropoffPoint, setDropoffPoint,
    parcelSize, setParcelSize,
    comment, setComment,
    codEnabled, setCodEnabled,
    codAmount, setCodAmount,
    insuranceEnabled, setInsuranceEnabled,
    insuranceAmount, setInsuranceAmount,
    creating, createError, createSuccess,
    handleCreateShipment,
  };
}

export function useTracking() {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingResult, setTrackingResult] = useState(null);
  const [tracking, setTracking] = useState(false);
  const [trackingError, setTrackingError] = useState(null);

  const handleTrack = async () => {
    if (!trackingNumber.trim()) { setTrackingError('Wpisz numer śledzenia'); return; }
    try {
      setTracking(true);
      setTrackingError(null);
      setTrackingResult(null);
      const response = await shippingService.trackShipment(trackingNumber.trim());
      if (response.success) {
        setTrackingResult(response.data);
      } else {
        setTrackingError(response.message || 'Przesyłka nie znaleziona');
      }
    } catch (error) {
      setTrackingError('Błąd śledzenia przesyłki');
    } finally {
      setTracking(false);
    }
  };

  return { trackingNumber, setTrackingNumber, trackingResult, tracking, trackingError, handleTrack };
}

export function useShipmentFilters(shipments) {
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  const filteredShipments = useMemo(() => {
    let filtered = [...shipments];
    if (filterStatus) {
      const statusGroup = SHIPMENT_STATUSES[filterStatus];
      if (statusGroup?.statuses) {
        filtered = filtered.filter(s => statusGroup.statuses.includes(s.status));
      }
    }
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(s =>
        (s.tracking_number || '').toLowerCase().includes(search) ||
        (s.receiver_name || '').toLowerCase().includes(search) ||
        (s.numer_zamowienia || '').toLowerCase().includes(search)
      );
    }
    return filtered;
  }, [shipments, filterStatus, searchTerm]);

  const totalPages = Math.ceil(filteredShipments.length / perPage);
  const startIndex = (currentPage - 1) * perPage;
  const paginatedShipments = filteredShipments.slice(startIndex, startIndex + perPage);

  return {
    filterStatus, setFilterStatus,
    searchTerm, setSearchTerm,
    currentPage, setCurrentPage,
    perPage, setPerPage,
    filteredShipments, paginatedShipments,
    totalPages, startIndex,
  };
}
